import { UseGuardStepHook } from "../useGuardStep";
import { useCurrentWorkspace, useWorkspaces, useWorkspacesInitializing } from "@/store";
import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useFlowState } from "../useFlowState";
import LoadingScreen from "@/components/LoadingScreen";

/**
 * useGuardWorkspace - guard and redirect validation for workspace-namespaced routes
 **/

export const useGuardWorkspace: UseGuardStepHook = () => {
    const { workspaceUrl } = useParams<{ workspaceUrl: string }>();
    const workspacesInitializing = useWorkspacesInitializing();
    const currentWorkspace = useCurrentWorkspace();
    const workspaces = useWorkspaces();
    const { computeUrlPath } = useFlowState();
    const location = useLocation();

    // Share routes (/p/) should always be allowed through
    const isShareRoute = location.pathname.includes("/p/");

    const authorizedWorkspaceUrl = useMemo(() => {
        if (!workspaceUrl) return false;
        if (isShareRoute) return true; // Share routes don't need workspace auth
        return workspaces?.some((w) => w.url === workspaceUrl);
    }, [workspaceUrl, workspaces, isShareRoute]);

    const authorizedWorkspace = useMemo(() => {
        if (isShareRoute) return true; // Share routes don't need workspace auth
        if (workspaceUrl && workspaceUrl === currentWorkspace?.url) return true;
        return authorizedWorkspaceUrl;
    }, [workspaceUrl, currentWorkspace, authorizedWorkspaceUrl, isShareRoute]);

    if (workspacesInitializing) {
        return {
            loading: true,
            passThrough: true,
            errors: [],
            redirect: null,
            LoadingComponent: LoadingScreen
        };
    }

    if (authorizedWorkspace) {
        return { loading: false, passThrough: true, errors: [], LoadingComponent: LoadingScreen };
    }

    const errors = [];
    if (!workspaceUrl) errors.push("No workspace specified");
    if (workspaceUrl && !authorizedWorkspaceUrl) errors.push("Workspace not authorized");

    return {
        loading: false,
        passThrough: false,
        errors,
        redirect: computeUrlPath({ workspaceUrl: "" }) + location.search,
        LoadingComponent: LoadingScreen
    };
};
